import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import { DomainDataType } from 'types/domain';
import {
    CleanedDomainData,
    DomainCreationRequest,
    DomainCreationResult,
} from '../types/domain-management.types';
import { BackendDomainsRepo } from '@monsido/modules/endpoints/api/backend_admin/backend-domains.repo';

@Injectable({
    providedIn: 'root',
})
export class DomainCreationService {

    constructor (
        private backendDomainsRepo: BackendDomainsRepo,
    ) {}

    /**
     * Create a new domain via API with enhanced error handling
     */
    createDomain (customerId: number, domainData: DomainCreationRequest): Observable<DomainCreationResult> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            noGlobal: 'true',
        });

        const payload = this.prepareDomainForCreation(domainData);

        return new Observable(observer => {
            this.backendDomainsRepo.create(customerId, payload, headers)
                .then((domain: DomainDataType) => {
                    observer.next({
                        success: true,
                        domain,
                    });
                    observer.complete();
                })
                .catch((error) => {
                    const result = this.processCreationError(error, domainData.name);
                    observer.next(result);
                    observer.complete();
                });
        });
    }

    /**
     * Create multiple domains from import data
     */
    createDomainsFromImport (customerId: number, domainsData: CleanedDomainData[]): Observable<DomainCreationResult[]> {
        const creationRequests = domainsData.map(domainData => {
            const request: DomainCreationRequest = {
                url: domainData.url,
                name: domainData.title || domainData.url,
                language: domainData.language || 'en',
                tags: [],
                description: domainData.notes || '',
                createdBy: 'import',
            };
            return this.createDomain(customerId, request);
        });

        // Execute all creation requests in parallel
        return new Observable(observer => {
            Promise.all(creationRequests.map(req => req.toPromise()))
                .then(results => {
                    observer.next(results);
                    observer.complete();
                })
                .catch(error => {
                    observer.error(error);
                });
        });
    }

    /**
     * Prepare domain data for API submission
     */
    private prepareDomainForCreation (domainData: DomainCreationRequest): DomainDataType {
        const payload: Partial<DomainDataType> = {
            url: this.normalizeUrl(domainData.url),
            title: domainData.name.trim(),
            language: domainData.language,
            active: true,
            timezone: 'UTC',
            notes: domainData.description?.trim() || '',
            labels: domainData.tags || [],
        };

        // Only include CMS if it has a valid value
        if (domainData.cms && domainData.cms.trim() !== '') {
            payload.cms = domainData.cms;
        }

        return payload as DomainDataType;
    }

    /**
     * Process and categorize creation errors
     */
    private processCreationError (error: unknown, domainName: string): DomainCreationResult {
        const errorMessage = this.extractErrorMessage(error);
        const validationErrors = this.extractValidationErrors(error);

        let errorType: 'validation' | 'network' | 'server' | 'permission' | 'unknown' = 'unknown';
        let retryable = false;

        if (this.isErrorWithMessage(error)) {
            const status = error.status;

            switch (status) {
                case 400:
                    errorType = 'validation';
                    retryable = false;
                    break;
                case 401:
                case 403:
                    errorType = 'permission';
                    retryable = false;
                    break;
                case 409:
                    errorType = 'validation';
                    retryable = false; // Duplicate domain
                    break;
                case 422:
                    errorType = 'validation';
                    retryable = false;
                    break;
                case 429:
                    errorType = 'server';
                    retryable = true; // Rate limited
                    break;
                case 500:
                case 502:
                case 503:
                case 504:
                    errorType = 'server';
                    retryable = true;
                    break;
                default:
                    if (status >= 500) {
                        errorType = 'server';
                        retryable = true;
                    } else if (status >= 400) {
                        errorType = 'validation';
                        retryable = false;
                    }
            }
        } else {
            // Network error or timeout
            errorType = 'network';
            retryable = true;
        }

        // Build a more detailed error message
        let finalErrorMessage = errorMessage;
        if (validationErrors.length > 0) {
            finalErrorMessage = `${errorMessage}: ${validationErrors.join(', ')}`;
        }

        return {
            success: false,
            error: finalErrorMessage,
            validationErrors,
            errorType,
            retryable,
        };
    }

    /**
     * Normalize URL for domain creation
     */
    private normalizeUrl (url: string): string {
        let normalizedUrl = url.trim().toLowerCase();

        if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
            normalizedUrl = 'https://' + normalizedUrl;
        }

        if (normalizedUrl.endsWith('/')) {
            normalizedUrl = normalizedUrl.slice(0, -1);
        }

        return normalizedUrl;
    }

    /**
     * Extract error message from API response
     */
    private extractErrorMessage (error: unknown): string {
        if (this.isErrorWithMessage(error)) {
            if (error.error?.message) {
                return error.error.message;
            }
            if (error?.message) {
                return error.message;
            }
            if (error.error && typeof error.error === 'string') {
                return error.error;
            }
            if (error?.status) {
                switch (error.status) {
                    case 400:
                        return 'Invalid domain data provided';
                    case 401:
                        return 'Authentication required';
                    case 403:
                        return 'Permission denied';
                    case 409:
                        return 'Domain already exists';
                    case 422:
                        return 'Validation failed';
                    case 500:
                        return 'Server error occurred';
                    default:
                        return `HTTP ${error.status}: ${error.statusText || 'Unknown error'}`;
                }
            }
        }
        return 'An unexpected error occurred while creating the domain';
    }

    /**
     * Extract validation errors from API response
     */
    private extractValidationErrors (error: unknown): string[] {
        const errors: string[] = [];

        if (this.isErrorWithMessage(error)) {
            if (error.error?.errors) {
                if (Array.isArray(error.error.errors)) {
                    errors.push(...error.error.errors);
                } else if (typeof error.error.errors === 'object' && error.error.errors !== null) {
                    Object.keys(error.error.errors).forEach(field => {
                        const errorObj = error.error?.errors as Record<string, unknown>;
                        const fieldErrors = errorObj[field];

                        // Clean up field name (remove quotes and brackets)
                        const cleanField = field.replace(/^\["?/, '').replace(/"?\]$/, '');

                        if (Array.isArray(fieldErrors)) {
                            errors.push(...fieldErrors.map(err => `${cleanField}: ${err}`));
                        } else {
                            errors.push(`${cleanField}: ${fieldErrors}`);
                        }
                    });
                }
            }

            if (error.error?.validation_errors && Array.isArray(error.error.validation_errors)) {
                errors.push(...error.error.validation_errors);
            }
        }

        return errors;
    }

    /**
     * Type guard for error objects
     */
    private isErrorWithMessage (error: unknown): error is {
        error?: {
            message?: string;
            errors?: unknown;
            validation_errors?: string[];
        };
        message?: string;
        status?: number;
        statusText?: string;
    } {
        return typeof error === 'object' && error !== null;
    }

    /**
     * Validate domain creation request before API call
     */
    validateCreationRequest (request: DomainCreationRequest): { valid: boolean; errors: string[] } {
        const errors: string[] = [];

        if (!request.url || request.url.trim().length === 0) {
            errors.push('URL is required');
        }

        if (!request.name || request.name.trim().length === 0) {
            errors.push('Name is required');
        }

        return {
            valid: errors.length === 0,
            errors,
        };
    }
}
